import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Button, Card, EmptyState, ErrorAlert, cx, useDebouncedCallback } from "@unizpay/ui";
import { Search, SearchX } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useMode } from "../lib/mode";
import { SOURCE_FILTERS } from "../lib/paymentSource";
import { useProfileApi } from "../lib/profile";
import type { PaymentSourceFilter, PaymentSummary, StatusGroup } from "../lib/types";
import { PaymentsTable } from "./PaymentsTable";
import { Tabs } from "./Tabs";

const PAGE_SIZE = 25;

const TABS: Array<{ status: StatusGroup | undefined; label: string }> = [
  { status: undefined, label: "All" },
  { status: "succeeded", label: "Succeeded" },
  { status: "open", label: "Open" },
  { status: "attention", label: "Needs attention" },
  { status: "closed", label: "Closed" },
];

function parseStatus(value: string | null): StatusGroup | undefined {
  return TABS.find((t) => t.status === value)?.status;
}

function parseSource(value: string | null): PaymentSourceFilter | undefined {
  return SOURCE_FILTERS.find((f) => f.source === value)?.source;
}

interface Filters {
  status?: StatusGroup;
  source?: PaymentSourceFilter;
  q?: string;
  cursors?: string[];
}

/**
 * Filters and paging live in the query string: `status`, `source`, `q`, and `after`, a
 * comma-separated stack of `starting_after` cursors (the API pages forward only, so Previous
 * pops the stack).
 */
function usePaymentsQueryState(path: string) {
  const { mode } = useMode();
  const [params, setParams] = useSearchParams();
  const status = parseStatus(params.get("status"));
  const source = parseSource(params.get("source"));
  const q = params.get("q") ?? "";
  const after = params.get("after") ?? "";
  const cursors = after.split(",").filter(Boolean);

  function search(next: Filters) {
    const p = new URLSearchParams();
    if (next.status) p.set("status", next.status);
    if (next.source) p.set("source", next.source);
    if (next.q) p.set("q", next.q);
    if (next.cursors?.length) p.set("after", next.cursors.join(","));
    return p;
  }

  // Cursors belong to one mode's data: start again at the first page when the mode changes.
  const pagedMode = useRef(mode);
  useEffect(() => {
    if (pagedMode.current === mode) return;
    pagedMode.current = mode;
    if (after) setParams(search({ status, source, q }), { replace: true });
  });

  return {
    status,
    source,
    q,
    cursors,
    hrefFor: (next: Filters) => {
      const s = search(next).toString();
      return s ? `${path}?${s}` : path;
    },
    setQuery: (value: string) => setParams(search({ status, source, q: value }), { replace: true }),
    setSource: (value: PaymentSourceFilter | undefined) => setParams(search({ status, source: value, q })),
    setCursors: (next: string[]) => setParams(search({ status, source, q, cursors: next })),
  };
}

/** Debounced search box that follows outside changes to its value. Also used on Transactions. */
export function SearchField({
  value,
  onChange,
  label = "Filter by payment ID",
  placeholder = "Payment ID or UnizPay ID",
}: {
  value: string;
  onChange: (q: string) => void;
  label?: string;
  placeholder?: string;
}) {
  const id = useId();
  const [text, setText] = useState(value);
  // Follow outside changes to `q` (top bar search, back button) without fighting the user's typing.
  const [seen, setSeen] = useState(value);
  if (value !== seen) {
    setSeen(value);
    setText(value);
  }
  const push = useDebouncedCallback((v: string) => {
    setSeen(v);
    onChange(v);
  }, 300);

  return (
    <div className="relative w-full sm:w-72">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-subtle"
      />
      <input
        id={id}
        type="search"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          push(e.target.value.trim());
        }}
        placeholder={placeholder}
        className="h-8 w-full rounded-md border border-line-strong bg-surface pr-2.5 pl-8 text-sm shadow-xs placeholder:text-fg-subtle focus:border-ring focus:outline-2 focus:outline-offset-0 focus:outline-ring/30"
      />
    </div>
  );
}

/** Source filter as a segmented control: one click, and the current choice always visible. */
function SourceFilter({
  value,
  onChange,
}: {
  value: PaymentSourceFilter | undefined;
  onChange: (source: PaymentSourceFilter | undefined) => void;
}) {
  return (
    <fieldset className="inline-flex max-w-full min-w-0 overflow-x-auto rounded-md border border-line-strong bg-surface p-0.5 shadow-xs">
      <legend className="sr-only">Filter by source</legend>
      {SOURCE_FILTERS.map((f) => (
        <button
          key={f.label}
          type="button"
          aria-pressed={f.source === value}
          onClick={() => onChange(f.source)}
          className={cx(
            "rounded px-2.5 py-1 text-[0.8125rem] font-medium whitespace-nowrap",
            f.source === value ? "bg-accent/10 text-accent" : "text-fg-muted hover:bg-subtle hover:text-fg",
          )}
        >
          {f.label}
        </button>
      ))}
    </fieldset>
  );
}

/**
 * Status tabs with counts, source filter, search, the payments table and pagination, all
 * kept in the URL.
 */
export function PaymentsBrowser({
  path,
  onDeactivate,
  empty,
}: {
  /** The page's own path, for the filter links. */
  path: string;
  /** The Deactivate row action on payment links. */
  onDeactivate?: (p: PaymentSummary) => void;
  /** Shown when there is nothing at all yet (first page, no filters). */
  empty: ReactNode;
}) {
  const { api, mode, key } = useProfileApi();
  const state = usePaymentsQueryState(path);
  const { status, source, q, cursors } = state;
  const cursor = cursors.at(-1);

  const payments = useQuery({
    queryKey: key("payments", { status, source, q, cursor }),
    queryFn: () =>
      api.payments(mode, { status, source, q: q || undefined, startingAfter: cursor, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const rows = payments.data?.data;
  const counts = payments.data?.counts;
  const last = rows?.at(-1);
  const page = cursors.length + 1;
  const filtered = Boolean(q || status || source);

  return (
    <>
      <Tabs
        label="Filter by status"
        items={TABS.map((t) => ({
          to: state.hrefFor({ status: t.status, source, q }),
          label: t.label,
          count: counts?.[t.status ?? "all"],
          active: t.status === status,
        }))}
      />

      <div className="my-4 flex flex-wrap items-center gap-3">
        <SearchField value={q} onChange={state.setQuery} />
        <SourceFilter value={source} onChange={state.setSource} />
        {q && (
          <p className="text-[0.8125rem] text-fg-muted sm:ml-auto">
            Results for <span className="font-medium text-fg">“{q}”</span>
          </p>
        )}
      </div>

      <Card flush>
        {payments.isError ? (
          <div className="p-4">
            <ErrorAlert error={payments.error} title="Could not load payments" />
          </div>
        ) : (
          <PaymentsTable
            rows={rows}
            loading={payments.isPending}
            dimmed={payments.isPlaceholderData}
            skeletonRows={8}
            onDeactivate={onDeactivate}
            empty={
              page > 1 ? (
                <EmptyState title="No more payments" />
              ) : filtered ? (
                <EmptyState
                  icon={SearchX}
                  title="No matching payments"
                  action={
                    <Link to={path} className="text-[0.8125rem] font-medium text-accent hover:underline">
                      Clear filters
                    </Link>
                  }
                >
                  Try another payment ID, status or source.
                </EmptyState>
              ) : (
                empty
              )
            }
          />
        )}
        {(page > 1 || payments.data?.has_more) && (
          <nav
            aria-label="Pagination"
            className="flex items-center justify-between gap-3 border-t border-line px-4 py-2.5 sm:px-5"
          >
            <span className="text-[0.8125rem] text-fg-muted">
              Page {page}
              {rows && rows.length > 0 && ` · ${rows.length} results`}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 1 || payments.isFetching}
                onClick={() => state.setCursors(cursors.slice(0, -1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!payments.data?.has_more || !last || payments.isFetching}
                onClick={() => last && state.setCursors([...cursors, last.id])}
              >
                Next
              </Button>
            </div>
          </nav>
        )}
      </Card>
    </>
  );
}
